/*
 * Copyright (c) 2024, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

export * from '@salesforce/retail-react-app/app/utils/product-utils';

import {getPriceData} from '@salesforce/retail-react-app/app/utils/product-utils';

/**
 * Find variant that matches the given variation parameters
 * @param {Object} product - Product object with variants
 * @param {Object} variationParams - Object with variation attribute IDs as keys and values
 * @returns {Object|null} Matching variant or null
 */
export const findVariantByParams = (product, variationParams) => {
  if (!product?.variants || !variationParams) {
    return null;
  }

  const matchingVariants = product.variants.filter((variant) => {
    if (!variant.variationValues) return false;

    return Object.keys(variationParams).every(
      (key) => variant.variationValues[key] === variationParams[key]
    );
  });

  return matchingVariants.length > 0 ? matchingVariants[0] : null;
};

/**
 * Get price data for a specific variation value
 * @param {Object} product - Product object with variants
 * @param {Object} currentVariationParams - Current variation parameters
 * @param {string} variationAttributeId - The variation attribute ID (e.g., 'size', 'color')
 * @param {string} variationValue - The new value to get price for
 * @param {Object} opts - Options for getPriceData (quantity, etc.)
 * @returns {Object|null} Price data object or null
 */
export const getVariantPriceForValue = (
  product,
  currentVariationParams,
  variationAttributeId,
  variationValue,
  opts = {}
) => {
  if (!product || !variationAttributeId || !variationValue) {
    return null;
  }

  const newVariationParams = {
    ...currentVariationParams,
    [variationAttributeId]: variationValue,
  };

  const variant = findVariantByParams(product, newVariationParams);

  if (variant) {
    return getPriceData(variant, opts);
  }

  // If no exact match, try to find variants that partially match
  // This handles cases where not all attributes are selected yet
  // We'll find the first variant that has this value for this attribute
  if (!product?.variants) {
    return null;
  }

  const partialMatchVariants = product.variants.filter((v) => {
    if (!v.variationValues) return false;

    const hasThisValue = v.variationValues[variationAttributeId] === variationValue;
    const matchesOtherParams = Object.keys(currentVariationParams).every(
      (key) => key === variationAttributeId || v.variationValues[key] === currentVariationParams[key]
    );

    return hasThisValue && matchesOtherParams;
  });

  if (partialMatchVariants.length > 0) {
    return getPriceData(partialMatchVariants[0], opts);
  }

  return null;
};

/**
 * Get the variant price data more directly
 * @param {Object} variant - Variant object
 * @param {Object} opts - Options for getPriceData
 * @returns {Object} Price data
 */
export const getVariantPriceData = (variant, opts = {}) => {
  if (!variant) return null;

  return getPriceData(variant, opts);
};

/**
 * Get the first orderable variant from a list of variants
 * @private
 * @param {Array} variants - Array of variant objects
 * @returns {Object|null} First orderable variant or null
 */
const getFirstOrderableVariant = (variants) => {
  if (!variants?.length) return null;

  return variants.find((variant) => variant?.orderable) || null;
};

/**
 * Find a variant by matching attributes, with fallback to first orderable
 * @private
 * @param {Array} variants - Array of variant objects
 * @param {Object} attributes - Attribute key-value pairs to match
 * @returns {Object|null} Matching variant or first orderable variant
 */
const findVariantByAttributes = (variants, attributes) => {
  if (!variants?.length) return null;

  const hasAttributes = attributes && Object.keys(attributes).length > 0;

  if (!hasAttributes) {
    return getFirstOrderableVariant(variants);
  }

  const matchedVariant = variants.find((variant) => {
    if (!variant?.variationValues || !variant.orderable) return false;

    return Object.keys(attributes).every(
      (key) => variant.variationValues[key] === attributes[key]
    );
  });

  return matchedVariant || getFirstOrderableVariant(variants);
};

/**
 * Select the first orderable variant based on URL parameters
 * @param {Object} product - Product object with variants
 * @param {Object} urlParams - URL parameters for variant selection
 * @returns {Object} Object containing selectedVariant and error (if any)
 */
export const selectFirstOrderableVariant = (product, urlParams) => {
  if (!product?.variants?.length) {
    return {
      selectedVariant: null,
      error: product ? 'Invalid product data: missing variants information' : null,
    };
  }

  const matchedVariant = findVariantByAttributes(product.variants, urlParams);

  if (!matchedVariant) {
    const hasAttributes = urlParams && Object.keys(urlParams).length > 0;

    return {
      selectedVariant: null,
      error: hasAttributes ?
        'No matching variant found for selected attributes' :
        'No orderable variants available for this product',
    };
  }

  return {
    selectedVariant: matchedVariant,
    error: null,
  };
};

/**
 * Determine if a product is a simple product (finished good) or complex product (requires configuration)
 * Uses the simpleProduct custom attribute to determine the product type
 *
 * @param {Object} product - Product object from SCAPI
 * @returns {boolean} True if simple product, false if complex product
 *
 * @example
 * const product = { c_simpleProduct: true };
 * isSimpleProduct(product); // returns true
 *
 * const complexProduct = { c_simpleProduct: false };
 * isSimpleProduct(complexProduct); // returns false
 */
export const isSimpleProduct = (product) => {
  if (!product) {
    return false;
  }

  // Check the simpleProduct custom attribute
  // Default to false (complex) if attribute is not set
  return product.c_simpleProduct === true;
};

/**
 * Determine if a product should show Cylindo 3D viewer
 *
 * Logic:
 * - Simple products: Never show 3D (always show image cycler)
 * - Complex products: Show 3D only if images are from Cylindo source
 *
 * @param {Object} product - Product object from SCAPI
 * @param {string} imageSource - Image source ('pim' or 'cylindo')
 * @returns {boolean} True if should show Cylindo 3D viewer
 *
 * @example
 * const complexProduct = { c_simpleProduct: false };
 * shouldShowCylindo3D(complexProduct, 'cylindo'); // returns true
 * shouldShowCylindo3D(complexProduct, 'pim'); // returns false
 *
 * const simpleProduct = { c_simpleProduct: true };
 * shouldShowCylindo3D(simpleProduct, 'cylindo'); // returns false (simple always shows cycler)
 */
export const shouldShowCylindo3D = (product, imageSource) => {
  if (!product || !imageSource || isSimpleProduct(product)) {
    return false;
  }

  // Complex products: Show 3D only if images are from Cylindo
  return imageSource === 'cylindo';
};
